// =============================================================================
// search-list.ts — Normalisation RUNTIME d'un filtre multi-valeurs lu dans l'URL.
// =============================================================================
// POURQUOI CE GARDE-FOU EXISTE (constaté au lot F1, le 2026-09-21, par un ST qui a blanchi l'écran).
//
// `validateSearch` d'une route TanStack Router ne SUPPRIME pas un paramètre : la search exposée à un
// composant est la FUSION de la search brute (portée par la route racine, qui n'a pas de validation)
// et du résultat validé de chaque route de la chaîne. Une clé que la validation OMET — parce que sa
// valeur est invalide — n'est donc pas retirée : la valeur BRUTE survit et atteint le composant.
//
// Cas vécu : `?fournisseur=93200005100017`. Le parseur par défaut JSON-parse chaque valeur, ce SIRET
// tout en chiffres devient un NOMBRE, la validation l'écarte (ce n'est pas une chaîne), et le nombre
// brut arrive quand même dans la prop typée `readonly string[] | undefined`. Le premier `.map` de
// l'écran jette `TypeError: o.map is not a function` et la page entière tombe sur l'écran d'erreur du
// routeur. Le code d'avant le lot F1 y survivait par accident (il ne faisait qu'une comparaison
// d'égalité sur la valeur) ; un filtre multi-valeurs itère, donc il doit se protéger.
//
// Règle : une URL tapée à la main, tronquée ou héritée d'une version antérieure NE DOIT JAMAIS mettre
// l'écran par terre. Ce qui n'est pas lisible comme une liste de chaînes non vides vaut « aucun
// filtre » (fail-safe : on montre tout, on ne cache rien en silence).

/**
 * Lit une valeur de search param comme une LISTE de chaînes non vides.
 *
 * - une liste ⇒ ses seuls éléments chaîne non vides ;
 * - une chaîne non vide ⇒ liste d'UN élément (rétro-compatibilité des URL à valeur unique) ;
 * - tout le reste (nombre, booléen, objet, `undefined`) ⇒ liste VIDE, c'est-à-dire aucun filtre.
 *
 * @param allowed Échelle FERMÉE facultative (fail-closed) : toute valeur hors échelle est écartée.
 */
export function toStringList<T extends string = string>(
  value: unknown,
  allowed?: readonly T[],
): readonly T[] {
  const candidates = Array.isArray(value) ? (value as unknown[]) : [value];
  const strings = candidates.filter(
    (candidate): candidate is T => typeof candidate === 'string' && candidate.length > 0,
  );
  return allowed === undefined
    ? strings
    : strings.filter((candidate) => allowed.includes(candidate));
}
